import React, { useState, useEffect, useContext } from "react";
import Paper from "@mui/material/Paper";
import { WizardView, WizardContextProvider } from "../Wizard";
import { Form } from "react-bootstrap";
import {
    Grid,
    Table,
    TableHeaderRow,
    TableFilterRow,
    PagingPanel,
    TableColumnResizing,
    Toolbar,
    TableSelection,
    TableColumnVisibility,
    ColumnChooser,
    TableTreeColumn,
} from "@devexpress/dx-react-grid-material-ui";
import {
    SelectionState,
    FilteringState,
    IntegratedFiltering,
    DataTypeProvider,
    SortingState,
    PagingState,
    IntegratedPaging,
    IntegratedSelection,
    IntegratedSorting,
    TreeDataState,
    CustomTreeData,
} from "@devexpress/dx-react-grid";

function OnboardPlantWizard(props) {
    const [items, setItems] = useState(props.items);
    const [selected, setSelected] = useState();
    const [columns, setColumns] = useState([
        { name: "name", title: "Name" },
        {
            name: "description",
            title: "Description",
        },
    ]);
    const [tableColumnExtensions] = useState([
        { columnName: "name", width: 300 },
    ]);
    const [defaultHiddenColumnNames] = useState(["description"]);

    useEffect(() => {
        (async () => {
            const hzones = [
                {
                    id: 1,
                    plant_id: 1,
                    parent_id: null,
                    name: "Default Zone",
                    description: null,
                    is_active: 1,
                    created_at: "2022-04-11T16:29:02.000000Z",
                    updated_at: "2022-04-11T16:29:02.000000Z",
                    deleted_at: null,
                    created_by: 2,
                    updated_by: 2,
                    deleted_by: null,
                    children: [],
                },
            ];
            setItems(hzones);
        })();
    }, []);

    return (
        <WizardContextProvider
            onClose={props.onClose}
            disableClose={props.disableClose}
        >
            <WizardView.Page title="Welcome" icon="plus">
                <p>
                    A{" "}
                    <span className="badge badge-primary">
                        <i className="fa fa-industry"></i> Plant
                    </span>{" "}
                    identifies your manufacturing unit. Your devices, data and
                    reports belong to a Plant.
                </p>
                <p>
                    You can choose to have multiple plants based on your
                    purchase plan.
                </p>
                <p>
                    <i>Click Next to add a new plant!</i>
                </p>
            </WizardView.Page>
            <WizardView.Page title="Plant Details" icon="industry">
                <Form>
                    <p>
                        Choose a name for you{" "}
                        <span className="badge badge-primary">
                            <i className="fa fa-industry"></i> Plant
                        </span>{" "}
                        that helps you identify it. This could be a factory or a
                        warehouse etc.
                    </p>
                    <Form.Group className="mb-3" controlId="plant.name">
                        <Form.Control
                            type="text"
                            placeholder="Plant Name (e.g. Unit A)"
                            onChange={(e) => {}}
                        />
                    </Form.Group>
                    <hr></hr>
                    <p>
                        Every Plant needs to have an administrative{" "}
                        <span className="badge badge-primary">
                            <i className="fa fa-user"></i> User
                        </span>{" "}
                        The administrator can carry out all possible tasks for a{" "}
                        <span className="badge badge-primary">
                            <i className="fa fa-industry"></i> Plant
                        </span>{" "}
                        , including creating more users.
                    </p>
                    <Form.Group className="mb-3" controlId="plant.admin_email">
                        <Form.Control
                            type="text"
                            placeholder="Administrative User Email"
                            onChange={(e) => {}}
                        />
                    </Form.Group>
                    <Form.Group
                        className="mb-3"
                        controlId="plant.admin_password"
                    >
                        <Form.Control
                            type="password"
                            placeholder="Administrative User Password"
                            onChange={(e) => {}}
                        />
                    </Form.Group>
                </Form>
            </WizardView.Page>
            <WizardView.Page
                title="Define Zones"
                icon="bullseye"
                skippable={true}
            >
                <p>
                    A{" "}
                    <span className="badge badge-primary">
                        <i className="fa fa-bullseye"></i> Zone
                    </span>{" "}
                    hierarchically segregates areas of your{" "}
                    <span className="badge badge-primary">
                        <i className="fa fa-industry"></i> Plant
                    </span>{" "}
                    for easier reporting. You can review and analyse your data
                    one or multiple selected Zones.
                </p>
                <p>
                    For instance, if your Plant has a building with two floors,
                    you can choose to view your data by each floor or aggregate
                    it by the buiilding.
                </p>
                <hr></hr>
                <p>
                    <i>
                        You can{" "}
                        <span className="text-primary">skip this step</span> if
                        you have only one zone!
                    </i>
                    <br></br>
                    <span className="text-sm">
                        A{" "}
                        <span className="badge badge-primary">
                            <i className="fa fa-bullseye"></i> Default Zone
                        </span>{" "}
                        will be created for your{" "}
                        <span className="badge badge-primary">
                            <i className="fa fa-industry"></i> Plant
                        </span>{" "}
                    </span>
                </p>
                <hr />
                <Paper>
                    {items && (
                        <Grid rows={items} columns={columns}>
                            <TreeDataState />
                            <CustomTreeData
                                getChildRows={(row, rootRows) =>
                                    row ? row.children : rootRows
                                }
                            ></CustomTreeData>
                            <SelectionState
                                selection={selected}
                                onSelectionChange={setSelected}
                            />
                            <IntegratedSelection></IntegratedSelection>
                            <Table columnExtensions={tableColumnExtensions} />
                            <TableColumnVisibility
                                defaultHiddenColumnNames={
                                    defaultHiddenColumnNames
                                }
                            />
                            <TableHeaderRow />
                            <TableSelection
                                showSelectionColumn={false}
                                selectByRowClick
                                highlightRow={true}
                            />
                            <TableTreeColumn for="name" />
                            <Toolbar />
                            <ColumnChooser />
                        </Grid>
                    )}
                </Paper>
            </WizardView.Page>
            <WizardView.Page
                title="Define Shifts"
                icon="clipboard"
                skippable={true}
            >
                <p>
                    A{" "}
                    <span className="badge badge-primary">
                        <i className="fa fa-clipboard"></i> Shift
                    </span>{" "}
                    defines working times for your{" "}
                    <span className="badge badge-primary">
                        <i className="fa fa-industry"></i> Plant
                    </span>
                    . Shifts are also used to report how efficiently your plant
                    is operating.
                </p>
                <p>
                    For instance you could have a <i>Morning Shift</i> defined
                    between 8.30 AM - 6.00 PM and an <i>Evening Shift</i>{" "}
                    defined between 8:30 PM - 6:00 AM on the next day
                </p>
                <hr></hr>
                <p>
                    <i>
                        You can{" "}
                        <span className="text-primary">skip this step</span> if
                        you have only one shift!
                    </i>
                    <br />
                    <span className="text-sm">
                        A{" "}
                        <span className="badge badge-primary">
                            <i className="fa fa-clipboard"></i> Default Shift
                        </span>{" "}
                        will be created for your{" "}
                        <span className="badge badge-primary">
                            <i className="fa fa-industry"></i> Plant
                        </span>{" "}
                    </span>
                </p>
                <hr></hr>
                <div className="row">
                    <div className="col-lg-9"></div>
                </div>
                <div className="col-lg-12 setShiftContianer">
                    <div className="setShift" id="shift_container"></div>
                </div>
            </WizardView.Page>
            <WizardView.Page title="Finish" icon="flag">
                <p>Great! It seems we have the required information!</p>
                <p>
                    <i>
                        Click on Finish to setup your{" "}
                        <span className="badge badge-primary">
                            <i className="fa fa-industry"></i> Plant
                        </span>{" "}
                    </i>
                </p>
            </WizardView.Page>
        </WizardContextProvider>
    );
}

export default OnboardPlantWizard;
